import { generateRandomString, sha256 } from '@core/utils/crypto';

// Storage keys
const CODE_VERIFIER_KEY = 'pkce_code_verifier';
const CODE_VERIFIER_RETRIEVED_KEY = 'pkce_code_verifier_retrieved';
const STATE_KEY = 'pkce_state';
const STATE_VERIFIED_KEY = 'pkce_state_verified';
const PROVIDER_ID_KEY = 'auth_provider_id';

// In-memory cache for code verifier
// This prevents the code verifier from being lost when the component re-renders
let cachedCodeVerifier: string | null = null;

/**
 * Generate a code verifier for PKCE
 */
export function generateCodeVerifier(): string {
  return generateRandomString(64);
}

/**
 * Generate a code challenge from a code verifier
 */
export async function generateCodeChallenge(codeVerifier: string): Promise<string> {
  return await sha256(codeVerifier);
}

/**
 * Store code verifier in session storage
 */
export function storeCodeVerifier(codeVerifier: string): void {
  sessionStorage.setItem(CODE_VERIFIER_KEY, codeVerifier);
}

/**
 * Get and clear code verifier from session storage
 *
 * This function is designed to be resilient to multiple calls,
 * which can happen in React's Strict Mode or when components
 * are re-rendered multiple times.
 */
export function getAndClearCodeVerifier(): string | null {
  // If we've already retrieved the code verifier, return the cached value
  if (cachedCodeVerifier) {
    return cachedCodeVerifier;
  }

  // Check if we've already retrieved the code verifier in a previous render
  const retrieved = sessionStorage.getItem(CODE_VERIFIER_RETRIEVED_KEY);

  if (retrieved === 'true') {
    // This is a subsequent call, but we don't have the cached value
    // This could happen if the page was refreshed
    return null;
  }

  // This is the first call, get the code verifier from session storage
  const verifier = sessionStorage.getItem(CODE_VERIFIER_KEY);

  if (verifier) {
    // Mark as retrieved but don't remove it yet
    // This allows subsequent calls to succeed
    sessionStorage.setItem(CODE_VERIFIER_RETRIEVED_KEY, 'true');

    // Cache the code verifier in memory
    cachedCodeVerifier = verifier;
  }

  return verifier;
}

/**
 * Store provider ID in session storage
 */
export function storeProviderId(providerId: string): void {
  sessionStorage.setItem(PROVIDER_ID_KEY, providerId);
}

/**
 * Get provider ID from session storage
 */
export function getProviderId(): string | null {
  return sessionStorage.getItem(PROVIDER_ID_KEY);
}

/**
 * Clear provider ID from session storage
 */
export function clearProviderId(): void {
  sessionStorage.removeItem(PROVIDER_ID_KEY);
}

/**
 * Generate and store state parameter with optional redirect URL
 */
export function generateAndStoreState(redirectUrl?: string): string {
  const state = generateRandomString(32);

  // Create state object that includes both CSRF protection and redirect info
  const stateData = {
    csrf: state,
    redirectUrl: redirectUrl || null,
  };

  // Encode the state data as base64 to include in the OAuth state parameter
  const encodedState = btoa(JSON.stringify(stateData));

  sessionStorage.setItem(STATE_KEY, state);

  return encodedState;
}

/**
 * Verify and clear state parameter
 *
 * This function is designed to be resilient to multiple calls,
 * which can happen in React's Strict Mode or when components
 * are re-rendered multiple times.
 */
export function verifyAndClearState(receivedState: string): boolean {
  // Check if state has already been verified
  const stateVerified = sessionStorage.getItem(STATE_VERIFIED_KEY);

  if (stateVerified === 'true') {
    return true;
  }

  const storedState = sessionStorage.getItem(STATE_KEY);

  if (!storedState) {
    return false;
  }

  try {
    // Decode the received state to extract the CSRF token
    const stateData = JSON.parse(atob(receivedState));

    if (!stateData.csrf || stateData.csrf !== storedState) {
      return false;
    }

    // Mark state as verified but don't remove it yet
    // This allows subsequent calls to succeed
    sessionStorage.setItem(STATE_VERIFIED_KEY, 'true');

    return true;
  } catch {
    // If decoding fails, fall back to direct comparison for backward compatibility
    if (storedState !== receivedState) {
      return false;
    }

    // Mark state as verified but don't remove it yet
    // This allows subsequent calls to succeed
    sessionStorage.setItem(STATE_VERIFIED_KEY, 'true');

    return true;
  }
}

/**
 * Extract redirect URL from state parameter
 */
export function extractRedirectFromState(receivedState: string): string | null {
  try {
    // Decode the received state to extract the redirect URL
    const stateData = JSON.parse(atob(receivedState));

    return stateData.redirectUrl || null;
  } catch {
    // If decoding fails, there's no redirect URL (backward compatibility)
    return null;
  }
}

/**
 * Clear all PKCE-related storage
 * This should be called after the authentication flow is complete
 */
export function clearPkceStorage(): void {
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(STATE_VERIFIED_KEY);
  sessionStorage.removeItem(CODE_VERIFIER_KEY);
  sessionStorage.removeItem(CODE_VERIFIER_RETRIEVED_KEY);

  // Clear the cached code verifier
  cachedCodeVerifier = null;
}
